import { useState } from 'react';
import { Lightbulb, Search, Loader, CheckCircle, AlertCircle, TrendingUp } from 'lucide-react';
import { explainFormationRecommendation, explainCertificationRecommendation } from '../../services/recommendations';

export default function IAExplicativePanel() {
  const [loading, setLoading] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [input, setInput] = useState({
    recommendationType: 'formation',
    recommendationId: '',
    score: 0.75
  });
  const [error, setError] = useState(null);

  const handleExplain = async () => {
    if (!input.recommendationId) {
      setError('Veuillez saisir un ID de recommandation');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Profil utilisateur simplifié
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userProfile = {
        competences: 'développement web javascript react node express',
        level: user.level || 'niveau_5',
        sector: user.sector || 'informatique'
      };

      let result;
      if (input.recommendationType === 'formation') {
        result = await explainFormationRecommendation(
          input.recommendationId,
          userProfile,
          parseFloat(input.score)
        );
      } else {
        result = await explainCertificationRecommendation(
          input.recommendationId,
          userProfile,
          parseFloat(input.score)
        );
      }

      setExplanation(result.explanation);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la génération de l\'explication');
      console.error('Erreur explication:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
          <Lightbulb className="w-5 h-5 text-yellow-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">IA Explicative</h2>
          <p className="text-sm text-gray-600">Justification transparente des recommandations</p>
        </div>
      </div>

      {/* Formulaire */}
      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
          <select
            value={input.recommendationType}
            onChange={(e) => setInput({ ...input, recommendationType: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
          >
            <option value="formation">Formation</option>
            <option value="certification">Certification RNCP</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            ID de la recommandation
          </label>
          <input
            type="text"
            value={input.recommendationId}
            onChange={(e) => setInput({ ...input, recommendationId: e.target.value })}
            placeholder="Ex: 1, 2, 3..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
          />
          <p className="mt-1 text-xs text-gray-500">
            Utilisez un ID de formation ou certification existant dans la base
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Score estimé (0 à 1)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            max="1"
            value={input.score}
            onChange={(e) => setInput({ ...input, score: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Bouton */}
      <button
        onClick={handleExplain}
        disabled={loading}
        className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
      >
        {loading ? (
          <>
            <Loader className="w-5 h-5 animate-spin" />
            Génération...
          </>
        ) : (
          <>
            <Search className="w-5 h-5" />
            Expliquer la recommandation
          </>
        )}
      </button>

      {/* Erreur */}
      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Explication */}
      {explanation && (
        <div className="mt-6 space-y-4">
          {/* Score */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4 border border-yellow-200">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-gray-900">Score de correspondance</span>
              <span className="text-3xl font-bold text-yellow-600">{explanation.score}%</span>
            </div>
          </div>

          {/* Raisons */}
          {explanation.reasons && explanation.reasons.length > 0 && (
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-600" />
                Raisons de la recommandation
              </h3>
              <ul className="space-y-2">
                {explanation.reasons.map((reason, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-1.5 flex-shrink-0" />
                    {reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Points forts */}
          {explanation.strengths && explanation.strengths.length > 0 && (
            <div className="border border-green-200 rounded-lg p-4 bg-green-50">
              <h3 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Points forts
              </h3>
              <ul className="space-y-2">
                {explanation.strengths.map((strength, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-green-800">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    {strength}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Points à améliorer */}
          {explanation.weaknesses && explanation.weaknesses.length > 0 && (
            <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
              <h3 className="font-semibold text-orange-900 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-orange-600" />
                Points à améliorer
              </h3>
              <ul className="space-y-2">
                {explanation.weaknesses.map((weakness, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-orange-800">
                    <AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                    {weakness}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Prochaines étapes */}
          {explanation.nextSteps && explanation.nextSteps.length > 0 && (
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
              <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Prochaines étapes recommandées
              </h3>
              <ul className="space-y-2">
                {explanation.nextSteps.map((step, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-blue-800">
                    <span className="font-bold text-blue-600 mt-0.5 flex-shrink-0">{index + 1}.</span>
                    {step}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {!loading && !explanation && !error && (
        <div className="mt-6 text-center py-12 text-gray-500">
          <Lightbulb className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p>Obtenez une explication détaillée d'une recommandation</p>
        </div>
      )}
    </div>
  );
}